import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Copy,
  Tag,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Shirt,
  Palette,
  BarChart2,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import MainNav from "@/components/MainNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  General: "bg-gray-100 text-gray-700",
  Club: "bg-blue-100 text-blue-700",
  Business: "bg-green-100 text-green-700",
  Retail: "bg-orange-100 text-orange-700",
  Event: "bg-purple-100 text-purple-700",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function QuoteTemplates() {
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "General",
    default_client_type: "Business",
    default_margin_percent: null,
    notes: "",
    garments: [],
    artwork_groups: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Expanded template cards
  const [expandedCards, setExpandedCards] = useState({});

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      
      const res = await axios.get(`${API}/templates?${params.toString()}`);
      setTemplates(res.data.templates || []);
      setTotal(res.data.total || 0);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error("Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  }, [search, categoryFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: "",
      description: "",
      category: "General",
      default_client_type: "Business",
      default_margin_percent: null,
      notes: "",
      garments: [],
      artwork_groups: [],
    });
    setIsModalOpen(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || "",
      description: template.description || "",
      category: template.category || "General",
      default_client_type: template.default_client_type || "Business",
      default_margin_percent: template.default_margin_percent,
      notes: template.notes || "",
      garments: template.garments || [],
      artwork_groups: template.artwork_groups || [],
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, formData);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/templates`, formData);
        toast.success("Template created");
      }
      setIsModalOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/templates/${template.id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      const newTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
      };
      delete newTemplate.id;
      delete newTemplate.created_at;
      delete newTemplate.updated_at;
      delete newTemplate.use_count;
      
      await axios.post(`${API}/templates`, newTemplate);
      toast.success("Template duplicated");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to duplicate template");
    }
  };

  const toggleExpanded = (templateId) => {
    setExpandedCards(prev => ({
      ...prev,
      [templateId]: !prev[templateId],
    }));
  };

  return (
    <div className="min-h-screen bg-aa-bg">
      <MainNav />

      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-aa-container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings" className="p-2 rounded-aa-md hover:bg-gray-100 transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </Link>
              <div className="p-2 rounded-aa-md bg-amber-100">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-aa-xl font-bold text-gray-900">Quote Templates</h1>
                <p className="text-aa-sm text-gray-500">
                  Create and manage reusable quote templates
                </p>
              </div>
            </div>
            <Button onClick={openCreateModal} className="gap-2" data-testid="create-template-btn">
              <Plus className="w-4 h-4" />
              New Template
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-aa-container mx-auto px-6 py-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-templates"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">
            {total} template{total !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Template Cards */}
      <div className="max-w-aa-container mx-auto px-6 pb-8">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No templates found</p>
            <Button onClick={openCreateModal} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Create your first template
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-aa-lg shadow-aa-card border border-gray-100 overflow-hidden"
                data-testid={`template-card-${template.id}`}
              >
                {/* Card Header */}
                <div className="p-4 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <Badge className={CATEGORY_COLORS[template.category] || CATEGORY_COLORS.General}>
                        {template.category}
                      </Badge>
                      {template.use_count > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <BarChart2 className="w-3 h-3 mr-1" />
                          Used {template.use_count}x
                        </Badge>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-500 mb-2">{template.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Shirt className="w-3 h-3" />
                        {template.garments?.length || 0} garment{template.garments?.length !== 1 ? "s" : ""}
                      </span>
                      <span className="flex items-center gap-1">
                        <Palette className="w-3 h-3" />
                        {template.artwork_groups?.length || 0} print group{template.artwork_groups?.length !== 1 ? "s" : ""}
                      </span>
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {template.default_client_type}
                      </span>
                      {template.default_margin_percent && (
                        <span>{(template.default_margin_percent * 100).toFixed(0)}% margin</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => duplicateTemplate(template)}
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(template)}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template)}
                      className="text-destructive hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(template.id)}
                    >
                      {expandedCards[template.id] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedCards[template.id] && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Garments */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Garments</h4>
                        {template.garments?.length > 0 ? (
                          <div className="space-y-1">
                            {template.garments.map((g, idx) => (
                              <div key={g.id || `garment-${idx}`} className="text-sm flex justify-between">
                                <span>
                                  {g.name || "Unnamed"} {g.colour && `(${g.colour})`}
                                </span>
                                <span className="text-gray-500">
                                  Qty: {g.quantity} @ ${g.cost?.toFixed(2) || "0.00"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No garments defined</p>
                        )}
                      </div>

                      {/* Print Groups */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Print Groups</h4>
                        {template.artwork_groups?.length > 0 ? (
                          <div className="space-y-1">
                            {template.artwork_groups.map((ag, idx) => (
                              <div key={ag.id || `artwork-${idx}`} className="text-sm">
                                <span className="font-medium">{ag.print_method}</span>
                                {ag.placement_name && (
                                  <span className="text-gray-500"> - {ag.placement_name}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No print groups defined</p>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {template.notes && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Notes</h4>
                        <p className="text-sm text-gray-600">{template.notes}</p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Created by {template.created_by || "Unknown"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(template.created_at)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? "Update the template details below."
                : "Create a new quote template. You can add garments and print groups by saving an existing quote as a template."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Club Uniform Package"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Brief description of this template"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories.length > 0 ? categories : ["General", "Club", "Business", "Retail", "Event"]).map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Default Client Type</Label>
                <Select
                  value={formData.default_client_type}
                  onValueChange={(v) => setFormData({ ...formData, default_client_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Promo">Promo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="margin">Default Margin % (optional)</Label>
              <Input
                id="margin"
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 35"
                value={formData.default_margin_percent ? (formData.default_margin_percent * 100).toFixed(0) : ""}
                onChange={(e) => setFormData({
                  ...formData,
                  default_margin_percent: e.target.value ? parseFloat(e.target.value) / 100 : null,
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes about this template"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* Show summary if editing */}
            {editingTemplate && (formData.garments?.length > 0 || formData.artwork_groups?.length > 0) && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-700 mb-1">Template Contents:</p>
                <p className="text-gray-600">
                  {formData.garments?.length || 0} garment(s), {formData.artwork_groups?.length || 0} print group(s)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  To update garments and print groups, save a quote as a new template.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-template-btn">
              {isSaving ? "Saving..." : editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
